<template>
	<div class="d-flex min-vh-100 align-items-center two-factor-authentication">
		<CContainer>
			<CRow>
				<CCol>
					<div class="two-factor-authentication-form">
						<div class="logo">
							<FontAwesomeIcon
								class="icon"
								:icon="['fas', 'user-lock']"
							/>
						</div>

						<CForm @submit.prevent="handleSubmit">
							<h1 class="form-heading">
								{{ $t('page.twofactor.form.title') }}
							</h1>
							<!-- eslint-disable-next-line vue/no-v-html -->
							<p class="form-description" v-html="$t('page.twofactor.form.description')">
							</p>
							<CInput
								v-model.trim="$v.code.$model"
								:is-valid="isCodeValid"
								:invalid-feedback="errorCodeText"
								class="form-input"
								:placeholder="$t('page.twofactor.form.input.code.placeholder')"
								data-test-id="input-code"
								maxlength="6"
								@input="clearVerifySocialError"
							>
								<template #prepend-content>
									<CIcon name="cil-lock-locked" />
								</template>
								<template #append-content>
									<CButton
										:disabled="isResendLoading || resendCountdown > 0"
										class="resend-button"
										@click.prevent="handleResend"
									>
										{{ resendCountdown > 0 ? $t('page.twofactor.form.button.countdown', { countdown: resendCountdown }) : $t('page.twofactor.form.button.resend') }}
									</CButton>
								</template>
							</CInput>
							<div class="text-center">
								<div class="button-group">
									<CButton
										color="secondary"
										class="back-button px-4"
										data-test-id="button-back"
										@click="onBack"
									>
										{{ $t('page.twofactor.form.button.back') }}
									</CButton>
									<CButton
										color="primary"
										class="submit-button px-4"
										data-test-id="button-submit"
										:disabled="isCodeValid || isLoading"
										@click="handleSubmit"
									>
										<template v-if="isLoading">
											<FontAwesomeIcon
												class="check-spinner-third"
												:icon="['fas', 'spinner-third']"
											/>
										</template>
										<template v-else>
											{{ $t('page.twofactor.form.button.submit') }}
										</template>
									</CButton>
								</div>
							</div>
						</CForm>
					</div>
				</CCol>
			</CRow>
		</CContainer>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { required, numeric, minLength, maxLength } from 'vuelidate/lib/validators';
import { pathOr } from '../assets/js/helpers';
import errorMessage from '../enums/errorMessage';
import { ROUTE_NAME } from '../enums/route';

export default {
	name: 'TwoFactorAuthentication',
	validations: {
		code: {
			required,
			numeric,
			minLength: minLength(6),
			maxLength: maxLength(6),
		},
	},
	data() {
		return {
			code: '',

			version: process.env.VUE_APP_VERSION,

			resendCountdown: 0, // countdown in seconds
			resendTimer: null,	// reference to the countdown interval
		};
	},
	computed: {
		...mapState({
			isLoading: (state) => state.auth.isLoading,
			errorVerify: (state) => state.auth.error,
			errorVerifyMessage: (state) => state.auth.errorMessage,

			isResendLoading: (state) => state.auth.resend.isLoading,
			errorResend: (state) => state.auth.resend.error,
			errorResendMessage: (state) => state.auth.resend.errorMessage,
			requestExpiredTime: (state) => state.auth.resend.requestExpiredTime,

			errorTwoFactorMessage: (state) => state.auth.twoFactor.errorMessage,
			redirect: (state) => state.auth.twoFactor.redirect,
		}),

		...mapGetters({
			getDefaultRouteName: 'profile/getDefaultRouteName',
		}),

		errorCode() {
			return this.errorVerify || pathOr(false, ['$v', 'code', '$error'])(this);
		},
		errorCodeText() {
			const codeValidation = this.$v.code;

			if (!codeValidation.required) {
				return this.$t(errorMessage.REQUIRED_FIELD);
			} else if (!codeValidation.numeric) {
				return this.$t(errorMessage.INVALID_NUMBER);
			} else if (!codeValidation.minLength || !codeValidation.maxLength) {
				return this.$t(errorMessage.INVALID_LENGTH);
			} else if (this.errorVerify) {
				return this.$t(this.errorVerifyMessage);
			}
			return null;
		},
		isCodeValid() {
			// If error return false, if not return null
			return this.errorCode ? false : null;
		},
	},
	mounted() {
		document.body.style.setProperty('--version', `"v${this.version}"`);
	},
	methods: {
		...mapActions({
			postSocialVerify: 'auth/postSocialVerify',
			postSocialResend: 'auth/postSocialResend',
			clearVerifySocialError: 'auth/clearVerifySocialError',
			clearTwoFactorError: 'auth/clearTwoFactorError',

			getProfile: 'profile/getProfile',
		}),

		onBack() {
			this.$router.push({ name: ROUTE_NAME.LOGOUT });
		},
		async handleResend() {
			// Do nothing if already loading
			if (this.isResendLoading) {
				return;
			}

			await this.postSocialResend();

			// Reset countdown to 60 seconds
			let remainingSeconds = 60;

			if (this.requestExpiredTime) {
				const expireAt = new Date(this.requestExpiredTime);
				expireAt.setHours(expireAt.getHours() + 7);
				const now = new Date();

				const diff = Math.floor((expireAt - now) / 1000);
				if (!isNaN(diff) && diff > 0) {
					remainingSeconds = diff;
				}
			}

			this.resendCountdown = remainingSeconds;

			// Clear any existing timer
			if (this.resendTimer) {
				clearInterval(this.resendTimer);
			}

			// Start countdown timer
			this.resendTimer = setInterval(() => {
				if (this.resendCountdown > 0) {
					this.resendCountdown--;
				} else {
					clearInterval(this.resendTimer);
					this.resendTimer = null;
				}
			}, 1000);
		},
		async handleSubmit() {
			if (this.isLoading) {
				return;
			}

			this.$v.$touch();

			if (!this.$v.$invalid) {
				await this.postSocialVerify({
					code: this.code,
				});

				if (!this.errorVerify) {
					await this.getProfile();
					// Clear errors
					this.clearTwoFactorError();

					const routeName = this.getDefaultRouteName;
					if (this.redirect) {
						if (this.redirect === '/login') {
							this.$router.push({ name: routeName });
						} else {
							this.$router.push(this.redirect);
						}
					} else {
						// Navigate using Vue Router (SPA style)
						this.$router.push({ name: routeName });
					}
				}
			}
		},
		beforeDestroy() {
			// Clear timer when component is destroyed
			if (this.resendTimer) {
				clearInterval(this.resendTimer);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.two-factor-authentication {
	position: relative;

	.logo {
		width: 88px;
		height: 88px;
		margin: 0 auto 24px;
		background-color: #eaeaea;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;

		.icon {
			font-size: rem(40);
			color: #fff;
			line-height: 1;
			margin: 0;
			text-align: center;
		}
	}


	&-form {
		background-color: #fff;
		padding: 24px;
		border-radius: 8px;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
		max-width: 360px;
		margin: 0 auto;

		.form-heading {
			font-size: 20px;
			text-align: center;
		}

		.form-description {
			font-size: 14px;
			text-align: center;
		}

		.form-input {
			margin-bottom: rem(20);

			// overwrite hide append icon
			::v-deep .is-invalid {
				background-image: none;
			}
		}
	}

	&::after {
		content: var(--version);
		position: fixed;
		bottom: 20px;
		right: 20px;
		opacity: 0.3;
		font-size: rem(14);
		color: #000;
		pointer-events: none;
		z-index: 9999;
		user-select: none;
		white-space: nowrap;
	}
}


.button-group {
	display: flex;
	gap: 8px;
}

.back-button,
.submit-button {
	flex: 1;
}

.submit-button {
	.check-spinner-third {
		margin-right: 4px;
		animation: spin 1s linear infinite;
	}
}

.resend-button {
	font-size: rem(12);
	font-weight: 600;
	color: $color-hypertext;
	padding: 0;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}
</style>
